import { useState, useEffect } from 'react'
import { formatVersion } from '../utils/version'
import { Upload, Trash2, FileBox, CheckCircle, XCircle } from 'lucide-react'

interface Version {
  id: number
  version: number
  file_name: string
  file_size: number
  file_hash: string
  status: 'pending' | 'gray' | 'full' | 'rolled_back'
  created_at: string
}

export default function Versions() {
  const [versions, setVersions] = useState<Version[]>([])
  const [loading, setLoading] = useState(true)
  const [uploading, setUploading] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [versionNum, setVersionNum] = useState('')

  // 从 URL 获取 appId
  const getAppId = () => {
    const pathParts = window.location.pathname.split('/')
    return pathParts[2] || 'smart_clock'
  }

  const isEncryptionRequired = () => getAppId() === 'usb_ax206_display'
  const [encrypt, setEncrypt] = useState(isEncryptionRequired())  // 加密选项

  useEffect(() => {
    if (isEncryptionRequired()) {
      setEncrypt(true)
    }
    loadVersions()
  }, [])

  const loadVersions = async () => {
    try {
      const appId = getAppId()
      const response = await fetch(`/api/admin/apps/${appId}/versions`)
      const data = await response.json()
      setVersions(data.versions || [])
    } catch (error) {
      console.error('加载版本失败:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleUpload = async () => {
    if (!selectedFile || !versionNum.trim()) {
      console.log('上传条件不满足:', { selectedFile, versionNum })
      return
    }

    // 验证版本号格式
    let versionValue: number
    if (versionNum.toLowerCase().startsWith('0x')) {
      // 十六进制格式
      const hexValue = versionNum.slice(2)
      if (!/^[0-9a-fA-F]{1,8}$/.test(hexValue)) {
        alert('版本号格式错误！请使用 1-8 位十六进制数，如 0x00101010')
        return
      }
      versionValue = parseInt(hexValue, 16)
    } else {
      // 十进制格式（兼容旧格式）
      versionValue = parseInt(versionNum, 10)
      if (isNaN(versionValue)) {
        alert('版本号格式错误！请输入有效的数字')
        return
      }
    }

    setUploading(true)
    try {
      const formData = new FormData()
      formData.append('file', selectedFile)
      formData.append('version', versionValue.toString())
      formData.append('encrypt', encrypt.toString())  // 添加加密选项

      const appId = getAppId()
      const response = await fetch(`/api/admin/apps/${appId}/versions`, {
        method: 'POST',
        body: formData
      })

      if (response.ok) {
        await loadVersions()
        setSelectedFile(null)
        setVersionNum('')
        setEncrypt(isEncryptionRequired())
      }
    } catch (error) {
      console.error('上传失败:', error)
    } finally {
      setUploading(false)
    }
  }

  const handleDelete = async (id: number) => {
    if (!confirm('确定要删除这个版本吗？')) return

    try {
      const response = await fetch(`/api/admin/versions/${id}`, {
        method: 'DELETE'
      })

      if (response.ok) {
        await loadVersions()
      }
    } catch (error) {
      console.error('删除失败:', error)
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <span className="text-yellow-400">待发布</span>
      case 'gray':
        return <span className="text-blue-400">灰度中</span>
      case 'full':
        return <span className="text-green-400"><CheckCircle size={14} className="inline" /> 全量</span>
      case 'rolled_back':
        return <span className="text-red-400"><XCircle size={14} className="inline" /> 回滚</span>
      default:
        return <span className="text-gray-400">未知</span>
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B'
    const k = 1024
    const sizes = ['B', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
  }

  if (loading) {
    return <div className="text-center py-12 text-gray-400">加载中...</div>
  }

  return (
    <div className="space-y-6">
      {/* 上传区域 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">上传新版本</h3>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-sm text-gray-400 mb-2">版本号（十六进制）</label>
            <input
              type="text"
              value={versionNum}
              onChange={(e) => setVersionNum(e.target.value)}
              placeholder="例如：0x00101010"
              className="input w-full font-mono"
            />
            <p className="text-xs text-gray-500 mt-1">
              格式：0x + 8 位十六进制数（4 字节），如 0x00101010
            </p>
          </div>
          <div className="col-span-2">
            <label className="block text-sm text-gray-400 mb-2">固件文件</label>
            <div className="flex gap-2 items-center">
              <input
                type="file"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null
                  console.log('文件选择:', file)
                  setSelectedFile(file)
                }}
                className="input flex-1"
                accept=".bin,.hex"
              />
              <label className="flex items-center gap-2 text-sm text-gray-400 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={encrypt}
                  onChange={(e) => setEncrypt(isEncryptionRequired() ? true : e.target.checked)}
                  disabled={isEncryptionRequired()}
                  className="w-4 h-4 rounded border-gray-600 bg-gray-700 text-blue-500 focus:ring-blue-500 disabled:opacity-60"
                />
                🔒 {isEncryptionRequired() ? '强制加密固件' : '加密固件'}
              </label>
              <button
                onClick={handleUpload}
                disabled={!selectedFile || !versionNum.trim() || uploading}
                className={`btn-primary flex items-center gap-2 ${(!selectedFile || !versionNum.trim() || uploading) ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload size={16} />
                {uploading ? '上传中...' : '上传'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 版本列表 */}
      <div className="card">
        <h3 className="text-lg font-semibold text-white mb-4">版本列表</h3>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>版本</th>
                <th>文件名</th>
                <th>文件大小</th>
                <th>文件哈希</th>
                <th>状态</th>
                <th>创建时间</th>
                <th>操作</th>
              </tr>
            </thead>
            <tbody>
              {versions.map((version) => (
                <tr key={version.id}>
                  <td className="font-mono">{formatVersion(version.version)}</td>
                  <td className="flex items-center gap-2">
                    <FileBox size={16} className="text-gray-400" />
                    {version.file_name}
                  </td>
                  <td>{formatFileSize(version.file_size)}</td>
                  <td className="font-mono text-xs">{version.file_hash.substring(0, 16)}...</td>
                  <td>{getStatusBadge(version.status)}</td>
                  <td>{new Date(version.created_at).toLocaleString('zh-CN')}</td>
                  <td>
                    <button
                      onClick={() => handleDelete(version.id)}
                      className="text-red-400 hover:text-red-300"
                      title="删除版本"
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
